// @noflow
import { getJson, delJson, postJson } from '../utilities/fetchUtils';

import type { ThunkAction } from '../types';
import type { WATCHLIST_SORTING, PostWatchlistResponse } from './types';

export const DEFAULT_WATCH_LIST_SORT: WATCHLIST_SORTING = 'date_added';

export const sortWatchList = (sort: WATCHLIST_SORTING): ThunkAction => ({ dispatch }) => {
  dispatch({ type: 'WATCHLIST_SORT', payload: sort });
};

export const forceLoad = (): ThunkAction => ({ dispatch }) =>
  dispatch({ type: 'WATCHLIST_FORCE_LOAD' });

export const updateWatchlist = sort => dispatch => dispatch({ type: 'UPDATE_WATCHLIST', sort });

export const fetchWatchlist = (
  sort?: WATCHLIST_SORTING = DEFAULT_WATCH_LIST_SORT,
): ThunkAction => ({ getState, dispatch, config }) => {
  if (process.env.IN_CHROMECAST_MODE) return undefined;

  const state = getState();
  const { viewer: { euid } = {} } = state.auth;
  const { currentProfile: { profileId, profile } = {} } = state.profiles;

  const activeProfileId = profile?.id || profileId;

  if (!euid || !activeProfileId) {
    return Promise.resolve(new Error('Not signed in or missing profile'));
  }

  dispatch({ type: 'REQUEST_WATCHLIST' });

  const url = `${config.session.radish_url}watchlist`;
  const data = {
    platform: 'dol',
    sort,
  };

  return getJson({ url, state, data })
    .then(payload => {
      dispatch({ type: 'RECEIVE_WATCHLIST', payload });
    })
    .catch((error: Error) => {
      console.warn('RESPONSE_WATCHLIST_ERROR', error);
      dispatch({ type: 'REQUEST_WATCHLIST_ERROR' });
    });
};

export const addToWatchList = (
  contentId: number,
  callback?: (boolean, boolean) => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const { viewer: { euid } = {} } = state.auth;
  const { currentProfile: { profileId } = {} } = state.profiles;

  if (!euid || !profileId) {
    return Promise.resolve(new Error('Not signed in or missing profile'));
  }

  dispatch({ type: 'REQUEST_ADD_TO_WATCH_LIST', contentId });

  const url = `${config.session.radish_url}watchlist`;
  const data = {
    platform: 'dol',
    content_id: contentId,
    contentSource: 'ott',
    profile_id: profileId,
  };

  return postJson({ url, state, data })
    .then((response: PostWatchlistResponse) => {
      if (response.error !== undefined) {
        throw Error(response.error);
      } else {
        dispatch({ type: 'RECEIVE_ADD_TO_WATCH_LIST', contentId });

        if (callback) {
          callback(true, true);
        }
        return response;
      }
    })
    .catch(e => {
      console.warn(`Can't add ${contentId} to the watchlist: ${e.message}`);
      dispatch({ type: 'REQUEST_ADD_TO_WATCH_LIST_ERROR', contentId });
      if (callback) {
        callback(true, false);
      }
    });
};

export const removeItemFromWatchList = (
  contentId: number,
  callback?: (boolean, boolean) => void,
  isListUpdateNeeded: boolean = true,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const { viewer: { euid, uuid } = {} } = state.auth;
  const { currentProfile: { profileId } = {} } = state.profiles;

  if (!euid || !profileId) {
    return Promise.resolve(new Error('Not signed in or missing profile'));
  }

  dispatch({ type: 'REQUEST_REMOVE_ITEM_FROM_WATCHLIST', contentId });

  // endpoint requires id to be sent in this format
  const watchListItemId = `${uuid}-${contentId}`;

  const url = `${config.session.radish_url}watchlist/${watchListItemId}`;
  const data = {
    platform: 'dol',
  };

  return delJson({ url, state, data })
    .then((response: PostWatchlistResponse) => {
      if (response.error !== undefined) {
        throw Error(response.error);
      } else {
        if (isListUpdateNeeded) {
          dispatch(forceLoad());
        }

        if (callback) {
          callback(false, true);
        }
      }

      dispatch({ type: 'RECEIVE_REMOVE_ITEM_FROM_WATCHLIST', contentId });
      return response;
    })
    .catch((error: Error) => {
      console.warn(`Fail to remove ${contentId} from watchlist: ${error.message}`);
      dispatch({ type: 'REQUEST_REMOVE_ITEM_FROM_WATCHLIST_ERROR', contentId });
      if (callback) {
        callback(false, false);
      }
    });
};

export const deleteAllFromWatchList = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const { viewer: { euid } = {} } = state.auth;
  let { currentProfile: { profileId, profile } = {} } = state.profiles;

  profileId = profile?.id || profileId;

  if (!euid || !profileId) {
    return Promise.resolve(new Error('Not signed in or missing profile'));
  }

  const url = `${
    config.session.radish_url
  }zeus/rest/v9/idp/dish/members/${euid}/profile/${profileId}/instances/dany/watchlist`;

  const data = { platform: 'dol' };

  dispatch({ type: 'REQUEST_REMOVE_ALL_FROM_WATCHLIST' });

  return delJson({ url, state, data })
    .then(() => {
      dispatch({ type: 'RECEIVE_REMOVE_ALL_FROM_WATCHLIST' });
    })
    .catch((error: Error) => {
      console.warn(`Fail to remove all from watchlist: ${error.message}`);
      dispatch({ type: 'REQUEST_REMOVE_ALL_FROM_WATCHLIST_ERROR' });
    });
};
